import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { screenToWorld } from '../canvas/viewport';
import { usePresence, type PresenceApi } from './usePresence';

const FALLBACK: PresenceApi = {
  self: null, others: [],
  setCursor: () => {}, clearCursor: () => {}, setSelection: () => {}, setViewport: () => {}, publishAgent: () => {},
};

const Ctx = createContext<PresenceApi>(FALLBACK);

/** Read the shared presence API (one awareness subscription for the whole app). */
export function usePresenceCtx(): PresenceApi { return useContext(Ctx); }

/**
 * Mounts ONE presence subscription and wires the local signals into it:
 *   - pointermove → cursor (in CANVAS coords, mapped through the current
 *     per-user viewport so peers at any zoom/pan land correctly);
 *   - the selection store → selection;
 *   - the local viewport → viewport (for follow-mode).
 * Children read everything via usePresenceCtx().
 */
export function PresenceProvider({ children }: { children: ReactNode }) {
  const api = usePresence();
  const selection = useCanvasStore((s) => s.selection);
  const viewport = useCanvasStore((s) => s.doc.viewport);

  // Publish local cursor (throttled inside the api) in canvas coords.
  useEffect(() => {
    const onMove = (e: PointerEvent): void => {
      const vp = useCanvasStore.getState().doc.viewport;
      const w = screenToWorld({ x: e.clientX, y: e.clientY }, vp);
      api.setCursor(w.x, w.y);
    };
    const onLeave = (): void => api.clearCursor();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('blur', onLeave);
    document.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('blur', onLeave);
      document.removeEventListener('pointerleave', onLeave);
    };
    // api identity changes each render but its methods read live refs — bind once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { api.setSelection(selection[0]); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selection]);
  useEffect(() => { api.setViewport(viewport); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [viewport]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
